import React, { useEffect, useState, useRef, useCallback } from "react";
import DOMPurify from "dompurify";
import { api, formatErr } from "./lib/api";
import { formatKSA } from "./lib/ksaTime";

const SUGGESTIONS = [
  "ليش انخفض الإيراد أمس؟",
  "أعطني توصيات لزيادة معدل التحويل لعملاء 59",
  "أي منتج أحتاج أراجع تسعيره؟",
  "كيف أعيد العملاء اللي زاروني مرة واحدة؟",
  "أعطني تنبؤ للأسبوع القادم وأسبابه",
];

export default function AIInsights() {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [asking, setAsking] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ai/insights/latest");
        if (data && data.text) setLatest(data);
      } catch (e) { /* noop */ }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const generate = async () => {
    setGenerating(true); setErr(null);
    try {
      const { data } = await api.post("/ai/insights");
      setLatest(data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || asking) return;
    setMessages((cur) => [...cur, { id: `u-${Date.now()}-${cur.length}`, role: "user", text: question }]);
    setInput("");
    setAsking(true);
    try {
      const history = messages.slice(-6).map((m) => ({ role: m.role === "user" ? "user" : "assistant", text: m.text }));
      const { data } = await api.post("/ai/ask", { question, history });
      setMessages((cur) => [...cur, { id: `b-${Date.now()}-${cur.length}`, role: "bot", text: data.answer }]);
    } catch (e) {
      setMessages((cur) => [...cur, { id: `e-${Date.now()}-${cur.length}`, role: "bot", text: "⚠ " + (formatErr(e.response?.data?.detail) || e.message) }]);
    } finally {
      setAsking(false);
    }
  };

  // Naive markdown→html (h2 + bullets + bold). Output is sanitized via DOMPurify.
  const renderBold = (text) => {
    const html = String(text).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong"], ALLOWED_ATTR: [] });
    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: clean }} />;
  };

  const renderMd = (md) => {
    const lines = String(md || "").split("\n");
    const out = [];
    let listBuf = [];
    const flushList = (key) => {
      if (listBuf.length) {
        out.push(
          <ul key={`ul-${key}`}>
            {listBuf.map((l, i) => (
              <li key={`li-${key}-${i}`}>{renderBold(l)}</li>
            ))}
          </ul>
        );
        listBuf = [];
      }
    };
    lines.forEach((line, idx) => {
      const t = line.trim();
      if (t.startsWith("## ")) {
        flushList(idx);
        out.push(<h2 key={`h-${idx}`}>{t.slice(3)}</h2>);
      } else if (/^[-•]\s/.test(t)) {
        listBuf.push(t.replace(/^[-•]\s+/, ""));
      } else if (/^\d+\.\s/.test(t)) {
        listBuf.push(t.replace(/^\d+\.\s+/, ""));
      } else if (t) {
        flushList(idx);
        out.push(<p key={`p-${idx}`}>{renderBold(t)}</p>);
      }
    });
    flushList("end");
    return out;
  };

  return (
    <div data-testid="ai-tab">
      <div className="ai-hero">
        <h2>🤖 المحلل الذكي</h2>
        <p>تحليل تلقائي عميق لبياناتك مدعوم بـ Claude Sonnet 4.5 — توصيات تنفيذية، اكتشاف أنماط، وأسئلة وأجوبة بالعربية.</p>
        {latest?.generated_at && (
          <div className="ai-meta">
            🕐 آخر تحليل: {formatKSA(new Date(latest.generated_at))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <button data-testid="btn-generate-insights" onClick={generate} disabled={generating} className="btn btn-primary">
          {generating ? <><span className="spinner" /> جارٍ التحليل...</> : "🔍 توليد تحليل جديد"}
        </button>
        {latest && <span style={{ fontSize: 12, color: "var(--muted)", alignSelf: "center" }}>يأخذ التحليل عادةً 10-25 ثانية</span>}
      </div>

      {err && <div className="al alr">⚠ {err}</div>}

      <div className="ai-card" data-testid="ai-insights-card">
        {loading && <div className="skel" style={{ height: 200 }} />}
        {!loading && !latest && (
          <div style={{ textAlign: "center", padding: 30, color: "var(--muted)" }}>
            لا يوجد تحليل بعد. اضغط "توليد تحليل جديد" أعلاه للبدء.
          </div>
        )}
        {!loading && latest && (
          <div className="markdown">{renderMd(latest.text)}</div>
        )}
      </div>

      <div className="st">💬 اسأل عن بياناتك</div>
      <div className="ai-chat" data-testid="ai-chat">
        <div className="ai-chat-head">
          🪄 مساعدك التحليلي · مدعوم بكلود
        </div>
        <div className="ai-chat-body" ref={chatRef}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 12, padding: 20 }}>
              ابدأ بسؤال أو اختر اقتراحاً من الأسفل ⬇
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id || `m-${i}`} className={`ai-msg ${m.role}`}>{m.text}</div>
          ))}
          {asking && (
            <div className="ai-msg bot" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span className="spinner" /> يفكّر...
            </div>
          )}
        </div>
        {messages.length === 0 && (
          <div className="ai-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <span key={s} className="ai-sg" onClick={() => ask(s)} data-testid={`ai-sg-${i}`}>{s}</span>
            ))}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="ai-chat-form">
          <input
            data-testid="ai-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="اكتب سؤالك بالعربية..."
          />
          <button data-testid="ai-send" type="submit" disabled={asking || !input.trim()} className="btn btn-primary">إرسال</button>
        </form>
      </div>
    </div>
  );
}
